/**
 * Events/Webhooks API
 * Handles webhook subscriptions, object types, and file triggers
 */
import { baseApi } from '@/app/baseApi';

import type {
  CreateObjectTypeRequest,
  CreateWebhookSubscriptionRequest,
  EventData,
  ObjectType,
  TriggerSubscription,
  UpdateObjectTypeRequest,
  WebhookSubscription,
} from '../types';
import {
  transformCreateObjectTypeRequest,
  transformCreateWebhookSubscriptionRequest,
  transformGetObjectTypesResponse,
  transformGetTriggerSubscriptionsResponse,
  transformGetWebhookSubscriptionsResponse,
  transformUpdateObjectTypeRequest,
} from './events.mappers';

export const eventsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Object Types (Webhook Objects)
    getAllObjectTypes: builder.query<ObjectType[], { tenantKey: string; appKey: string }>({
      query: ({ tenantKey, appKey }) => ({
        url: `${tenantKey}/${appKey}/objecttypes/all`,
        endpoint: 'data',
      }),
      transformResponse: transformGetObjectTypesResponse,
      providesTags: ['Event'],
    }),

    getOwnedObjectTypes: builder.query<ObjectType[], { tenantKey: string; appKey: string }>({
      query: ({ tenantKey, appKey }) => ({
        url: `${tenantKey}/${appKey}/objecttypes/owned`,
        endpoint: 'data',
      }),
      transformResponse: transformGetObjectTypesResponse,
      providesTags: ['Event'],
    }),

    createObjectType: builder.mutation<
      void,
      { tenantKey: string; appKey: string; data: CreateObjectTypeRequest }
    >({
      query: ({ tenantKey, appKey, data }) => ({
        url: `${tenantKey}/${appKey}/objecttypes`,
        method: 'POST',
        body: transformCreateObjectTypeRequest(data),
        endpoint: 'data',
      }),
      invalidatesTags: ['Event'],
    }),

    updateObjectType: builder.mutation<
      void,
      { tenantKey: string; appKey: string; name: string; data: UpdateObjectTypeRequest }
    >({
      query: ({ tenantKey, appKey, name, data }) => ({
        url: `${tenantKey}/${appKey}/objecttypes/${name}`,
        method: 'PUT',
        body: transformUpdateObjectTypeRequest(data),
        endpoint: 'data',
      }),
      invalidatesTags: ['Event'],
    }),

    deleteObjectType: builder.mutation<void, { tenantKey: string; appKey: string; name: string }>({
      query: ({ tenantKey, appKey, name }) => ({
        url: `${tenantKey}/${appKey}/objecttypes/${name}`,
        method: 'DELETE',
        endpoint: 'data',
      }),
      invalidatesTags: ['Event'],
    }),

    // Webhook Subscriptions
    getWebhookSubscriptions: builder.query<
      WebhookSubscription[],
      { tenantId: string; appId: string }
    >({
      query: ({ tenantId, appId }) => ({
        url: `${tenantId}/${appId}/webhooks/subscriptions`,
        endpoint: 'transformer',
      }),
      transformResponse: transformGetWebhookSubscriptionsResponse,
      providesTags: ['Event'],
    }),

    createWebhookSubscription: builder.mutation<
      void,
      {
        tenantId: string;
        appId: string;
        envId?: string;
        baseId?: string;
        data: CreateWebhookSubscriptionRequest;
      }
    >({
      query: ({ tenantId, appId, envId, baseId, data }) => {
        // URL webhook (no environment/recipe)
        if (!envId || !baseId) {
          return {
            url: `${tenantId}/${appId}/webhooks/subscriptions`,
            method: 'POST',
            body: transformCreateWebhookSubscriptionRequest(data),
            endpoint: 'transformer',
          };
        }
        // Recipe webhook (with environment and recipe)
        return {
          url: `${tenantId}/${appId}/webhooks/environments/${envId}/subscriptions/${baseId}`,
          method: 'POST',
          body: transformCreateWebhookSubscriptionRequest(data),
          endpoint: 'transformer',
        };
      },
      invalidatesTags: ['Event'],
    }),

    deleteWebhookSubscription: builder.mutation<
      void,
      { tenantId: string; appId: string; subscriptionGuid: string }
    >({
      query: ({ tenantId, appId, subscriptionGuid }) => ({
        url: `${tenantId}/${appId}/webhooks/subscriptions/${subscriptionGuid}`,
        method: 'DELETE',
        endpoint: 'transformer',
      }),
      invalidatesTags: ['Event'],
    }),

    // Trigger Subscriptions (File Upload Triggers)
    getTriggerSubscriptions: builder.query<TriggerSubscription[], string>({
      query: (appId) => ({
        url: `getalltriggers/${appId}`,
        endpoint: 'transformer',
      }),
      transformResponse: transformGetTriggerSubscriptionsResponse,
      providesTags: ['Event'],
    }),

    clearTriggerSubscription: builder.mutation<void, { envId: string; transBaseId: string }>({
      query: ({ envId, transBaseId }) => ({
        url: `environments/${envId}/cleartrigger/${transBaseId}`,
        method: 'GET',
        endpoint: 'transformer',
      }),
      invalidatesTags: ['Event'],
    }),

    // Events
    createEvent: builder.mutation<void, { tenantKey: string; appKey: string; data: EventData }>({
      query: ({ tenantKey, appKey, data }) => ({
        url: `${tenantKey}/${appKey}/events`,
        method: 'POST',
        body: data,
        endpoint: 'data',
      }),
    }),

    // Webhook Subscription Timeseries
    getSubscriptionTimeseries: builder.query<
      unknown,
      { tenantKey: string; appKey: string; subscriptionGuid: string }
    >({
      query: ({ tenantKey, appKey, subscriptionGuid }) => ({
        url: `${tenantKey}/${appKey}/webhooks/subscriptions/${subscriptionGuid}/timeseries`,
        endpoint: 'data',
      }),
    }),
  }),
});

export const {
  useGetAllObjectTypesQuery,
  useGetOwnedObjectTypesQuery,
  useCreateObjectTypeMutation,
  useUpdateObjectTypeMutation,
  useDeleteObjectTypeMutation,
  useGetWebhookSubscriptionsQuery,
  useCreateWebhookSubscriptionMutation,
  useDeleteWebhookSubscriptionMutation,
  useGetTriggerSubscriptionsQuery,
  useClearTriggerSubscriptionMutation,
  useCreateEventMutation,
  useGetSubscriptionTimeseriesQuery,
} = eventsApi;
